import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowLeft, ShieldAlert, Clock, PlayCircle, StopCircle, History,
    RefreshCcw, KeyRound, AlertTriangle, Unlock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";

/**
 * SupportAccessAdmin — owner tool for time-boxed cross-tenant reads.
 *
 * Backend: `/api/admin/support-access/{start,end,current,history}`.
 * Every action is audit-logged (see `audit` collection). While a grant
 * is active, `auth.get_current_user()` pivots the caller's tenant_id
 * to the target so all reads land on the customer's data.
 */
const TTL_OPTIONS = [5, 15, 30, 60];

export default function SupportAccessAdmin() {
    const { user, refresh } = useAuth();
    const [state, setState] = useState({
        loading: true, current: null, history: [], tenants: [],
    });
    const [form, setForm] = useState({ tenant_id: "", reason: "", ttl_minutes: 15 });
    const [busy, setBusy] = useState(false);
    const [now, setNow] = useState(Date.now());
    const [unlockEmail, setUnlockEmail] = useState("");
    const [unlockBusy, setUnlockBusy] = useState(false);

    const unlockUser = async (e) => {
        e.preventDefault();
        const email = unlockEmail.trim().toLowerCase();
        if (!/.+@.+\..+/.test(email)) { toast.error("Enter a valid email."); return; }
        if (!window.confirm(`Clear the login lockout for ${email}?`)) return;
        setUnlockBusy(true);
        try {
            const { data } = await api.post("/admin/support-access/unlock-user", { email });
            toast.success(`Lockout cleared for ${data.email} (${data.cleared_lockout_rows} row${data.cleared_lockout_rows === 1 ? "" : "s"} removed).`);
            setUnlockEmail("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUnlockBusy(false);
        }
    };

    // Tick every second so the "expires in" countdown updates live.
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    const load = async () => {
        setState((s) => ({ ...s, loading: true }));
        try {
            const [cur, hist, tenants] = await Promise.all([
                api.get("/admin/support-access/current"),
                api.get("/admin/support-access/history", { params: { limit: 50 } }),
                api.get("/super-admin/tenants").catch(() => ({ data: { tenants: [] } })),
            ]);
            setState({
                loading: false,
                current: cur.data?.active || null,
                history: hist.data?.grants || [],
                tenants: (tenants.data?.tenants || []).filter((t) => t.id !== user?.tenant_id),
            });
        } catch (err) {
            setState((s) => ({ ...s, loading: false }));
            toast.error(formatApiError(err));
        }
    };

    useEffect(() => { load(); }, []);

    const startGrant = async (e) => {
        e.preventDefault();
        if (!form.tenant_id) { toast.error("Pick a target tenant."); return; }
        if (form.reason.trim().length < 10) { toast.error("Reason must be at least 10 characters."); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/admin/support-access/start", {
                tenant_id: form.tenant_id,
                reason: form.reason.trim(),
                ttl_minutes: Number(form.ttl_minutes) || 15,
            });
            toast.success(`Support access granted — expires ${fmtDateTime(data.grant.expires_at)}`);
            setForm({ tenant_id: "", reason: "", ttl_minutes: 15 });
            await load();
            await refresh(); // pull the pivoted tenant_id into AuthContext
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const endGrant = async () => {
        if (!window.confirm("End the active support session now? Your view will revert to your own workspace.")) return;
        setBusy(true);
        try {
            await api.post("/admin/support-access/end");
            toast.success("Support session ended. Reverted to your workspace.");
            await load();
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const activeRemainingMs = useMemo(() => {
        if (!state.current?.expires_at) return 0;
        return Math.max(0, new Date(state.current.expires_at).getTime() - now);
    }, [state.current, now]);

    const remainingText = useMemo(() => {
        if (activeRemainingMs <= 0) return "expired";
        const m = Math.floor(activeRemainingMs / 60000);
        const s = Math.floor((activeRemainingMs % 60000) / 1000);
        return `${m}m ${s.toString().padStart(2, "0")}s`;
    }, [activeRemainingMs]);

    return (
        <div className="max-w-6xl mx-auto p-6" data-testid="support-access-admin">
            <Link to="/dashboard" className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 mb-3">
                <ArrowLeft size={12} /> Back to dashboard
            </Link>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="text-xs font-semibold tracking-wider text-stone-500 uppercase mb-1 flex items-center gap-1.5">
                        <ShieldAlert size={12} /> Super Admin · Support Access
                    </div>
                    <h1 className="font-display text-4xl text-stone-900 leading-none mt-2">Time-boxed cross-tenant reads.</h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl leading-relaxed">
                        Grant yourself temporary, audited access to another workspace to investigate a
                        support ticket. Every start/end is logged with your email, IP, target tenant and
                        reason. Sessions auto-expire — you don&apos;t need to remember to end them.
                    </p>
                </div>
                <button
                    onClick={load}
                    data-testid="sa-refresh"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-2 text-xs font-semibold text-stone-700 hover:border-stone-400"
                >
                    <RefreshCcw size={11} /> Refresh
                </button>
            </div>

            {/* Active grant panel */}
            {state.current ? (
                <div
                    className="mb-8 rounded-2xl border-2 border-amber-400 bg-amber-50 p-5"
                    data-testid="sa-active-panel"
                >
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-full bg-amber-100 border border-amber-300 shrink-0">
                            <KeyRound size={16} className="text-amber-800" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold tracking-wider text-amber-900 uppercase mb-1">
                                Support session active
                            </div>
                            <div className="text-lg font-semibold text-stone-900">
                                Viewing <span className="text-amber-900">{state.current.target_tenant_name || state.current.tenant_id}</span>
                            </div>
                            <div className="text-sm text-stone-700 mt-2 italic">&ldquo;{state.current.reason}&rdquo;</div>
                            <div className="text-xs text-stone-600 mt-3 flex flex-wrap gap-4">
                                <span className="inline-flex items-center gap-1"><Clock size={11} /> expires in <b className="font-mono tabular-nums" data-testid="sa-countdown">{remainingText}</b></span>
                                <span>started {fmtDateTime(state.current.started_at)}</span>
                                <span>TTL {state.current.ttl_minutes}m</span>
                            </div>
                        </div>
                        <Button
                            onClick={endGrant}
                            disabled={busy}
                            className="bg-stone-900 hover:bg-stone-800 text-white shrink-0"
                            data-testid="sa-end-btn"
                        >
                            <StopCircle size={13} className="mr-1.5" /> End session
                        </Button>
                    </div>
                </div>
            ) : (
                <form
                    onSubmit={startGrant}
                    className="mb-8 rounded-2xl border border-stone-200 bg-white p-5"
                    data-testid="sa-start-form"
                >
                    <div className="flex items-center gap-2 mb-4">
                        <PlayCircle size={16} className="text-stone-700" />
                        <h2 className="text-lg font-semibold text-stone-900">Start a support session</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="md:col-span-2">
                            <Label className="eyebrow">Target workspace *</Label>
                            <Select
                                value={form.tenant_id}
                                onValueChange={(v) => setForm({ ...form, tenant_id: v })}
                            >
                                <SelectTrigger data-testid="sa-tenant-select">
                                    <SelectValue placeholder="Pick a tenant to enter…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {state.tenants.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-stone-500">No other tenants yet.</div>
                                    ) : state.tenants.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.name} · {t.admin_email}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="eyebrow">Session length</Label>
                            <Select
                                value={String(form.ttl_minutes)}
                                onValueChange={(v) => setForm({ ...form, ttl_minutes: Number(v) })}
                            >
                                <SelectTrigger data-testid="sa-ttl-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TTL_OPTIONS.map((m) => (
                                        <SelectItem key={m} value={String(m)}>{m} minutes</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="mb-4">
                        <Label className="eyebrow">Reason / ticket reference * <span className="normal-case text-[10px] text-stone-500">(minimum 10 characters, appears in audit trail)</span></Label>
                        <Textarea
                            value={form.reason}
                            onChange={(e) => setForm({ ...form, reason: e.target.value })}
                            placeholder="e.g. Investigating support ticket #ZD-421 — customer says cards are missing after the July migration."
                            className="min-h-[80px]"
                            required
                            minLength={10}
                            data-testid="sa-reason"
                        />
                    </div>

                    <div className="rounded-lg bg-stone-50 border border-stone-200 p-3 mb-4 text-[11px] text-stone-600 flex gap-2 items-start">
                        <AlertTriangle size={12} className="mt-0.5 text-amber-600 shrink-0" />
                        <span>
                            You will see the target workspace&apos;s <b>real data</b> — every read is
                            attributed to you. Do not export, screenshot or share anything without
                            written customer consent. The session ends automatically when the
                            timer hits zero.
                        </span>
                    </div>

                    <Button
                        type="submit"
                        disabled={busy}
                        className="bg-stone-900 hover:bg-stone-800 text-white"
                        data-testid="sa-start-btn"
                    >
                        <PlayCircle size={13} className="mr-1.5" />
                        {busy ? "Starting…" : "Start support session"}
                    </Button>
                </form>
            )}

            {/* Unlock user (self-serve 429 clear) */}
            <form
                onSubmit={unlockUser}
                className="mb-8 rounded-2xl border border-stone-200 bg-white p-5"
                data-testid="unlock-user-panel"
            >
                <div className="flex items-center gap-2 mb-2">
                    <Unlock size={16} className="text-stone-700" />
                    <h2 className="text-lg font-semibold text-stone-900">Unlock a locked-out user</h2>
                </div>
                <p className="text-xs text-stone-500 mb-4 max-w-2xl leading-relaxed">
                    Clears the brute-force lockout (HTTP 429 &ldquo;Too many failed
                    attempts&rdquo;) for a specific email. Also resets the user&apos;s
                    failed-attempt counter. Every unlock is audit-logged.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                        type="email"
                        value={unlockEmail}
                        onChange={(e) => setUnlockEmail(e.target.value)}
                        placeholder="locked-out user's email"
                        required
                        className="flex-1"
                        data-testid="unlock-email-input"
                    />
                    <Button
                        type="submit"
                        disabled={unlockBusy || !unlockEmail}
                        className="bg-stone-900 hover:bg-stone-800 text-white shrink-0"
                        data-testid="unlock-submit"
                    >
                        <Unlock size={13} className="mr-1.5" />
                        {unlockBusy ? "Clearing…" : "Clear lockout"}
                    </Button>
                </div>
            </form>

            {/* History */}
            <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="sa-history">
                <div className="px-5 py-3 border-b border-stone-100 flex items-center gap-2">
                    <History size={14} className="text-stone-500" />
                    <h3 className="text-sm font-semibold text-stone-900">Recent sessions</h3>
                    <span className="text-xs text-stone-500 ml-auto">{state.history.length} shown</span>
                </div>
                <table className="dense-table w-full">
                    <thead>
                        <tr>
                            <th>When</th>
                            <th>Admin</th>
                            <th>Target workspace</th>
                            <th>Reason</th>
                            <th>Duration</th>
                            <th>Ended</th>
                        </tr>
                    </thead>
                    <tbody>
                        {state.loading ? (
                            <tr><td colSpan={6} className="text-center text-stone-400 py-6">Loading…</td></tr>
                        ) : state.history.length === 0 ? (
                            <tr><td colSpan={6} className="text-center text-stone-500 py-8">No support sessions yet.</td></tr>
                        ) : state.history.map((g) => (
                            <tr key={g.id} data-testid={`sa-row-${g.id}`}>
                                <td className="text-[12px] tabular-nums">{fmtDateTime(g.started_at)}</td>
                                <td className="text-[12px]">{g.granted_to_email}</td>
                                <td className="text-[12px]">
                                    <div className="font-medium">{g.target_tenant_name || g.tenant_id}</div>
                                    <div className="text-[10px] text-stone-500 font-mono truncate max-w-[180px]">{g.tenant_id}</div>
                                </td>
                                <td className="text-[12px] italic text-stone-700 max-w-[260px] truncate" title={g.reason}>&ldquo;{g.reason}&rdquo;</td>
                                <td className="text-[12px]">{g.ttl_minutes}m</td>
                                <td className="text-[11px] text-stone-600">
                                    {g.ended_at ? (
                                        <>
                                            <div>{fmtDateTime(g.ended_at)}</div>
                                            <div className="text-[10px] text-stone-400 uppercase">{g.ended_reason || "ended"}</div>
                                        </>
                                    ) : (
                                        <span className="text-emerald-700 font-semibold text-[11px]">still active</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
